import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Lock } from 'lucide-react';
import { GOLD, GOLD_RGBA, INPUT_BG } from '../constants/booking';
import { STRIPE_PAYMENT_ELEMENT_OPTIONS } from '../config/stripe';

const labelStyle = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: '600',
  color: '#F5F0E8',
  marginBottom: '0.5rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: INPUT_BG,
  color: '#F5F0E8',
  border: '1px solid #4b5563',
  borderRadius: '0.75rem',
  padding: '0.875rem 1rem',
  fontSize: '1rem',
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const handleInputFocus = (e) => {
  e.target.style.borderColor = GOLD;
  e.target.style.boxShadow = '0 0 0 2px rgba(201, 168, 76, 0.2)';
};

const handleInputBlur = (e) => {
  e.target.style.borderColor = '#4b5563';
  e.target.style.boxShadow = 'none';
};

const SectionHeader = ({ title }) => (
  <div style={{ marginBottom: '1rem' }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '0.75rem',
      }}
    >
      <CreditCard size={18} color={GOLD} />
      <h3
        style={{
          fontSize: '1rem',
          fontWeight: '700',
          color: '#F5F0E8',
          margin: 0,
        }}
      >
        {title}
      </h3>
    </div>
    <div style={{ height: '1px', background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
  </div>
);

const PaymentForm = ({
  onSuccess,
  onError,
  isProcessing,
  customerInfo,
  clientSecret,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const paymentSubmittedRef = React.useRef(false);
  const [billingInfo, setBillingInfo] = useState({
    billingAddress: '',
    billingCity: '',
    billingState: 'Arizona',
    billingZip: '',
  });

  const handleElementLoadError = (event) => {
    const message =
      event?.error?.message ||
      'Unable to load the card form. Please refresh and try again.';
    console.error('Stripe PaymentElement load error:', event?.error);
    setError(message);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (paymentSubmittedRef.current || processing || isProcessing) {
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    if (!billingInfo.billingAddress.trim()) {
      setError('Billing address is required');
      return;
    }
    if (!billingInfo.billingCity.trim()) {
      setError('Billing city is required');
      return;
    }
    if (!billingInfo.billingZip.trim()) {
      setError('Billing ZIP code is required');
      return;
    }
    if (!/^\d{5}(-\d{4})?$/.test(billingInfo.billingZip.trim())) {
      setError('Please enter a valid ZIP code (5 digits or 5+4 format)');
      return;
    }

    if (!clientSecret) {
      setError('Payment not initialized. Please refresh the page.');
      setProcessing(false);
      return;
    }

    if (!termsAccepted) {
      setError('You must agree to the Terms of Service before completing payment.');
      return;
    }

    paymentSubmittedRef.current = true;
    setProcessing(true);
    setError(null);

    try {
      if (!stripe) {
        throw new Error('Stripe is not initialized. Please refresh the page and try again.');
      }

      if (!clientSecret || !clientSecret.startsWith('pi_')) {
        throw new Error('Invalid payment session. Please refresh the page and try again.');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: customerInfo.fullName,
              email: customerInfo.email,
              address: {
                line1: billingInfo.billingAddress,
                city: billingInfo.billingCity,
                state: billingInfo.billingState,
                postal_code: billingInfo.billingZip,
                country: 'US',
              },
            },
          },
          return_url: `${window.location.origin}/booking-success`,
        },
      });

      if (stripeError) {
        let userMessage = stripeError.message;
        if (stripeError.type === 'card_error') {
          userMessage = `Card error: ${stripeError.message}`;
        } else if (stripeError.type === 'validation_error') {
          userMessage = `Validation error: ${stripeError.message}`;
        } else if (stripeError.message?.includes('connection')) {
          userMessage =
            'Unable to connect to payment processor. Please check your internet connection and try again.';
        }
        throw new Error(userMessage);
      }

      if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
      } else if (paymentIntent.status === 'requires_action') {
        paymentSubmittedRef.current = false;
        setProcessing(false);
        return;
      } else {
        throw new Error(`Payment status: ${paymentIntent.status}. Please try again.`);
      }
    } catch (err) {
      paymentSubmittedRef.current = false;

      let errorMessage = err.message || 'An error occurred while processing your payment.';
      if (
        err.message &&
        (err.message.includes('connection') ||
          err.message.includes('network') ||
          err.message.includes('fetch'))
      ) {
        errorMessage =
          'Unable to connect to payment processor. Please check your internet connection and try again.';
      }

      setError(errorMessage);
      onError(errorMessage);
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="dd-stripe-form" style={{ width: '100%' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <SectionHeader title="Billing Address" />

        <div className="dd-stripe-billing-grid" style={{ marginBottom: '1rem' }}>
          <div>
            <label style={labelStyle}>Street Address *</label>
            <input
              type="text"
              value={billingInfo.billingAddress}
              onChange={(e) =>
                setBillingInfo((prev) => ({ ...prev, billingAddress: e.target.value }))
              }
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              style={inputStyle}
              placeholder="123 Main Street"
              required
            />
          </div>

          <div>
            <label style={labelStyle}>City *</label>
            <input
              type="text"
              value={billingInfo.billingCity}
              onChange={(e) =>
                setBillingInfo((prev) => ({ ...prev, billingCity: e.target.value }))
              }
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              style={inputStyle}
              placeholder="Phoenix"
              required
            />
          </div>
        </div>

        <div className="dd-stripe-billing-grid">
          <div>
            <label style={labelStyle}>State</label>
            <input
              type="text"
              value={billingInfo.billingState}
              disabled
              style={{
                ...inputStyle,
                backgroundColor: '#141414',
                color: '#9ca3af',
                cursor: 'not-allowed',
              }}
            />
          </div>

          <div>
            <label style={labelStyle}>ZIP Code *</label>
            <input
              type="text"
              value={billingInfo.billingZip}
              onChange={(e) =>
                setBillingInfo((prev) => ({ ...prev, billingZip: e.target.value }))
              }
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              style={inputStyle}
              placeholder="85001"
              pattern="[0-9]{5}(-[0-9]{4})?"
              required
            />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '1.75rem' }}>
        <SectionHeader title="Card Information" />

        <div
          className="dd-stripe-element"
          style={{
            padding: '1.25rem',
            border: `1px solid ${GOLD_RGBA}, 0.25)`,
            borderRadius: '0.75rem',
            backgroundColor: INPUT_BG,
          }}
        >
          <PaymentElement
            options={STRIPE_PAYMENT_ELEMENT_OPTIONS}
            onLoadError={handleElementLoadError}
          />
        </div>

        {error && (
          <div
            style={{
              color: '#fca5a5',
              fontSize: '0.875rem',
              marginTop: '0.75rem',
              padding: '0.75rem 1rem',
              backgroundColor: 'rgba(127, 29, 29, 0.25)',
              border: '1px solid rgba(248, 113, 113, 0.35)',
              borderRadius: '0.5rem',
            }}
          >
            {error}
          </div>
        )}
      </div>

      <div style={{ margin: '16px 0' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#F5F0E8', fontSize: '0.875rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            required
            style={{ marginTop: '0.2rem', flexShrink: 0 }}
          />
          <span>
            I agree to the{' '}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: GOLD }}>
              Terms of Service
            </Link>
          </span>
        </label>
      </div>

      <button
        type="submit"
        disabled={!stripe || processing || isProcessing || !termsAccepted}
        style={{
          width: '100%',
          backgroundColor: processing || isProcessing ? '#4b5563' : GOLD,
          color: '#000000',
          border: 'none',
          borderRadius: '0.75rem',
          padding: '1rem 2rem',
          fontSize: '1.0625rem',
          fontWeight: '700',
          cursor: processing || isProcessing ? 'not-allowed' : 'pointer',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
        }}
      >
        {processing || isProcessing ? (
          <>
            <div
              style={{
                width: '20px',
                height: '20px',
                border: '2px solid transparent',
                borderTop: '2px solid #000000',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            Processing Payment...
          </>
        ) : (
          <>
            Pay Deposit
            <CreditCard size={20} />
          </>
        )}
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '1rem',
          color: '#9ca3af',
          fontSize: '0.75rem',
        }}
      >
        <Lock size={12} style={{ marginRight: '0.25rem' }} />
        Your payment information is secure and encrypted
      </div>
    </form>
  );
};

const StripePayment = ({
  onSuccess,
  onError,
  isProcessing,
  customerInfo,
  clientSecret,
  bookingId,
}) => {
  return (
    <PaymentForm
      onSuccess={onSuccess}
      onError={onError}
      isProcessing={isProcessing}
      customerInfo={customerInfo}
      clientSecret={clientSecret}
      bookingId={bookingId}
    />
  );
};

export default StripePayment;
